import React from 'react'
import {
  Container,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box,
  GlobalStyles,
  styled,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useAuth } from '../../contexts/AuthContext'

/**
 * Option A: Wrap the default KeyboardArrowDownIcon in a component that explicitly sets its color to white.
 */
const WhiteArrowDownIcon = (props) => (
  <KeyboardArrowDownIcon
    {...props}
    style={{ color: '#ffffff', ...(props.style || {}) }}
  />
)

/**
 * Modern, Rich StyledSelect:
 * - Lighter gradient from #324755 to #465c6b
 * - White border on hover/focus
 * - Ensures the dropdown icon is white
 */
const StyledSelect = styled(Select)(() => ({
  background: 'linear-gradient(135deg, #324755 0%, #465c6b 100%)',
  borderRadius: 10,
  color: '#ffffff',
  boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',

  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.25)',
  },

  '& .MuiSelect-select': {
    paddingTop: 14,
    paddingBottom: 14,
    paddingLeft: 16,
    paddingRight: 40,
    fontWeight: 600,
  },

  // Make the border white on hover/focus
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ffffff',
    borderRadius: 10,
    borderWidth: 2,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ffffff',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ffffff',
  },

  // Force the icon to be white in case the above IconComponent isn't enough
  '& .MuiSelect-icon': {
    color: '#ffffff',
  },
  // Specifically for the "outlined" variant icon
  '& .MuiSelect-iconOutlined': {
    color: '#ffffff',
  },
}))

/**
 * ThreePhaseGrid:
 * 6-row x 4-column:
 * Row 1 (Forecast/Nowcast/Postcast) => entire cell clickable if enabled
 * Row 2 (Timeline)
 * Rows 3-5 (Main Data, Output, Use Cases) => #C8D1D3
 * Row 6 (Accuracy)
 */
const ThreePhaseGrid = ({ selectedEvent, onPhaseClick }) => {
  const isForecastEnabled =
    selectedEvent?.foreCast === true || selectedEvent?.foreCast === 'true'
  const isNowcastEnabled =
    selectedEvent?.nowCast === true || selectedEvent?.nowCast === 'true'
  const isPostcastEnabled =
    selectedEvent?.postCast === true || selectedEvent?.postCast === 'true'

  const rows = [
    // 1) Heading Row
    {
      label: '',
      forecast: {
        text: 'Forecast',
        phaseFlag: 'F',
        enabled: isForecastEnabled,
      },
      nowcast: { text: 'Nowcast', phaseFlag: 'N', enabled: isNowcastEnabled },
      postcast: {
        text: 'Postcast',
        phaseFlag: 'P',
        enabled: isPostcastEnabled,
      },
      isHeading: true,
    },
    // 2) Timeline Row (background now same as main data)
    {
      label: 'Timeline',
      forecast: { text: 'Days Prior to Event', timelineColor: '#C8D1D3' },
      nowcast: { text: 'During Event', timelineColor: '#C8D1D3' },
      postcast: { text: '1–2 Weeks After Event', timelineColor: '#C8D1D3' },
      isTimeline: true,
    },
    // 3) Main Data
    {
      label: 'Main Data',
      forecast: {
        icon: '/assets/images/icons/icon-row1-col1.png',
        text: 'Weather Prediction Data + Damage Plain',
      },
      nowcast: {
        icon: '/assets/images/icons/icon-row1-col2.png',
        text: 'Sensor Data + Other Attributes',
      },
      postcast: {
        icon: '/assets/images/icons/icon-row1-col3.png',
        text: 'SAR Imagery + Meta Attributes',
      },
      isDataRow: true,
    },
    // 4) Output
    {
      label: 'Output',
      forecast: {
        icon: '/assets/images/icons/icon-row2-col1.png',
        text: 'Expected Damages and Losses',
      },
      nowcast: {
        icon: '/assets/images/icons/icon-row2-col2.png',
        text: 'Structure-Level Damage Level',
      },
      postcast: {
        icon: '/assets/images/icons/icon-row2-col3.png',
        text: 'Structure-Level Damage Type & Extent',
      },
      isDataRow: true,
    },
    // 5) Use Cases
    {
      label: 'Use Cases',
      forecast: {
        icon: '/assets/images/icons/icon-row3-col1.png',
        text: 'Anticipatory Preparedness',
      },
      nowcast: {
        icon: '/assets/images/icons/icon-row3-col2.png',
        text: 'Situational Awareness + Federal Aid Application',
      },
      postcast: {
        icon: '/assets/images/icons/icon-row3-col3.png',
        text: 'Claim Processing + Data Reporting',
      },
      isDataRow: true,
    },
    // 6) Accuracy
    {
      label: 'Accuracy',
      forecast: { text: 'Order of Magnitude' },
      nowcast: { text: 'Ballpark' },
      postcast: { text: 'Within a Margin of Error' },
      isAccuracy: true,
    },
  ]

  // Colors & styling
  const headingEnabledBg = '#324755'
  const headingEnabledText = '#ffffff'
  const headingDisabledBg = '#C8D1D3'
  const headingDisabledText = '#2f2f2f'
  const timelineText = '#2f2f2f'
  const dataRowBg = '#C8D1D3'
  const defaultTextColor = '#2f2f2f'
  const accuracyText = '#324755'
  const cardShadow = '0 4px 12px rgba(0, 0, 0, 0.08)'
  const cardHoverShadow = '0 8px 16px rgba(0, 0, 0, 0.12)'

  // Generate styling for each cell
  const getCellStyle = (row, colKey) => {
    if (row.isHeading && colKey === 'label') {
      return { backgroundColor: 'transparent' }
    }
    if (row.isHeading && colKey !== 'label') {
      const isEnabled = row[colKey]?.enabled
      return {
        minHeight: 70,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isEnabled ? headingEnabledBg : headingDisabledBg,
        color: isEnabled ? headingEnabledText : headingDisabledText,
        fontWeight: 'bold',
        textAlign: 'center',
        boxShadow: cardShadow,
        borderRadius: 1,
        cursor: isEnabled ? 'pointer' : 'not-allowed',
        opacity: isEnabled ? 1 : 0.6,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        ':hover': {
          transform: isEnabled ? 'translateY(-2px)' : 'none',
          boxShadow: isEnabled ? cardHoverShadow : cardShadow,
        },
      }
    }
    if (row.isTimeline && colKey !== 'label') {
      const bg = row[colKey]?.timelineColor || dataRowBg
      return {
        minHeight: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: bg,
        color: timelineText,
        fontWeight: 'bold',
        textAlign: 'center',
        boxShadow: cardShadow,
        borderRadius: 1,
      }
    }
    if (colKey === 'label') {
      return {
        p: 2,
        color: defaultTextColor,
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
      }
    }
    if (row.isDataRow) {
      return {
        backgroundColor: dataRowBg,
        color: defaultTextColor,
        boxShadow: cardShadow,
        borderRadius: 1,
        textAlign: 'left',
      }
    }
    if (row.isAccuracy) {
      return {
        backgroundColor: 'transparent',
        color: accuracyText,
        fontWeight: 'bold',
        textAlign: 'center',
        minHeight: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }
    }
    // Fallback
    return {
      backgroundColor: '#f9f9f9',
      color: defaultTextColor,
      boxShadow: cardShadow,
      borderRadius: 1,
      textAlign: 'left',
    }
  }

  // Renders the content inside each cell
  const renderCellContent = (row, colKey) => {
    const cell = row[colKey] || {}
    const { text, icon, phaseFlag } = cell

    // If heading row & not the label col => clickable cell
    if (row.isHeading && colKey !== 'label' && phaseFlag) {
      return (
        <Typography variant="h6" sx={{ p: 2, fontWeight: 'bold' }}>
          {text}
        </Typography>
      )
    }
    if (row.isTimeline || colKey === 'label' || row.isAccuracy) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 'inherit' }}>
            {colKey === 'label' ? row.label : text}
          </Typography>
        </Box>
      )
    }
    // Data row
    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          ':hover': {
            transform: 'translateY(-2px)',
            boxShadow: cardHoverShadow,
          },
        }}
      >
        {icon && (
          <Box
            component="img"
            src={icon}
            alt={text}
            sx={{ width: 40, height: 40 }}
          />
        )}
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {text}
        </Typography>
      </Box>
    )
  }

  // Handle entire top row cell click
  const handleHeadingCellClick = (row, colKey) => {
    const cell = row[colKey] || {}
    const { phaseFlag, enabled } = cell
    if (row.isHeading && colKey !== 'label' && phaseFlag && enabled) {
      onPhaseClick(phaseFlag)
    }
  }

  return (
    <Box sx={{ width: '100%', overflowX: { xs: 'auto', md: 'visible' } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '150px repeat(3, 1fr)',
          },
          gap: 2,
          justifyContent: 'center',
          minWidth: { xs: 300, md: 'auto' },
        }}
      >
        {rows.map((row, rowIndex) => (
          <React.Fragment key={rowIndex}>
            {/* Label Cell */}
            <Box sx={getCellStyle(row, 'label')}>
              {renderCellContent(row, 'label')}
            </Box>

            {/* Forecast Cell */}
            <Box
              sx={getCellStyle(row, 'forecast')}
              onClick={() => handleHeadingCellClick(row, 'forecast')}
            >
              {renderCellContent(row, 'forecast')}
            </Box>

            {/* Nowcast Cell */}
            <Box
              sx={getCellStyle(row, 'nowcast')}
              onClick={() => handleHeadingCellClick(row, 'nowcast')}
            >
              {renderCellContent(row, 'nowcast')}
            </Box>

            {/* Postcast Cell */}
            <Box
              sx={getCellStyle(row, 'postcast')}
              onClick={() => handleHeadingCellClick(row, 'postcast')}
            >
              {renderCellContent(row, 'postcast')}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  )
}

/**
 * SelectionScreen:
 * - Single "Select Event" text
 * - Modern gradient dropdown with white border on hover/focus
 * - White dropdown arrow
 * - Entire top row is clickable if enabled
 */
const SelectionScreen = ({
  value,
  onChange,
  subtitles = [],
  selectedEvent,
  onPhaseClick,
}) => {
  const { keycloak } = useAuth()

  return (
    <>
      <GlobalStyles
        styles={{
          html: { margin: 0, padding: 0, width: '100%' },
          body: { margin: 0, padding: 0, width: '100%', minHeight: '100%' },
          '#root': { width: '100%', minHeight: '100%' },
        }}
      />
      <Box
        sx={{
          backgroundColor: '#ffffff',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 2,
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            mt: 2,
            textAlign: 'center',
          }}
        >
          {/* Richer Dropdown Container */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            <Paper
              elevation={4}
              sx={{
                p: 3,
                borderRadius: 10,
                background: 'linear-gradient(135deg, #324755 0%, #465c6b 100%)',
                width: '100%',
                maxWidth: 480,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                transform: 'translateY(0)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                ':hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.3)',
                },
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: '#ffffff',
                  fontWeight: 600,
                  textAlign: 'center',
                  mb: 1,
                }}
              >
                Select Event
              </Typography>
              <FormControl variant="outlined" fullWidth>
                <StyledSelect
                  label=""
                  value={value}
                  onChange={onChange}
                  IconComponent={WhiteArrowDownIcon}
                  displayEmpty
                  sx={{
                    fontWeight: 600,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background:
                          'linear-gradient(135deg, #324755 0%, #465c6b 100%)',
                        color: '#ffffff',
                        borderRadius: 2,
                        border: '1px solid rgba(255,255,255,0.1)',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
                        backdropFilter: 'blur(6px)',
                      },
                    },
                  }}
                >
                  {subtitles.map((subtitle, index) => (
                    <MenuItem
                      key={subtitle.id || index}
                      value={subtitle.name || 'unknown'}
                      sx={{
                        background: 'transparent',
                        color: '#ffffff',
                        py: 2,
                        transition: 'background 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.15)',
                        },
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 500 }}>
                          {subtitle.name || 'Unnamed Event'}
                        </Typography>
                        {subtitle.description && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.5,
                              opacity: 0.8,
                            }}
                          >
                            {subtitle.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </Paper>
          </Box>

          {/* Three-Phase Grid */}
          <ThreePhaseGrid
            selectedEvent={selectedEvent}
            onPhaseClick={onPhaseClick}
          />
        </Container>
      </Box>
    </>
  )
}

export default SelectionScreen
